import React from 'react';
import { Link } from 'react-router-dom';

// A tiny, dependency-free Markdown renderer — enough for our legal documents:
// h1/h2/h3, paragraphs, unordered/ordered lists, blockquotes, horizontal rules,
// **bold**, *italic*, `code`, and links. Internal links (starting with "/") use
// React Router's <Link>; external / mailto links use a plain <a>. It is NOT a full
// CommonMark parser — just the subset these documents use.

const ACCENT = '#d4b483'; // bronze
const TEXT = '#f4f1ea'; // cream
const MUTED = 'rgba(244,241,234,0.82)';

const linkStyle: React.CSSProperties = { color: ACCENT, textDecoration: 'underline', textUnderlineOffset: '2px' };

// ── Inline: bold / italic / code / links ──────────────────────────────────────
function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  const re = /\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*|__([^_]+)__|\*([^*]+)\*|`([^`]+)`/g;
  let last = 0;
  let i = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const key = `${keyPrefix}-${i++}`;
    if (m[1] !== undefined) {
      const label = m[1];
      const url = m[2];
      if (url.startsWith('/')) {
        nodes.push(<Link key={key} to={url} style={linkStyle}>{label}</Link>);
      } else {
        const external = /^https?:\/\//.test(url);
        nodes.push(
          <a
            key={key}
            href={url}
            style={linkStyle}
            {...(external ? { target: '_blank', rel: 'noreferrer' } : {})}
          >
            {label}
          </a>
        );
      }
    } else if (m[3] !== undefined || m[4] !== undefined) {
      nodes.push(<strong key={key} style={{ color: TEXT, fontWeight: 600 }}>{m[3] ?? m[4]}</strong>);
    } else if (m[5] !== undefined) {
      nodes.push(<em key={key}>{m[5]}</em>);
    } else if (m[6] !== undefined) {
      nodes.push(
        <code
          key={key}
          style={{
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: '0.9em',
            background: 'rgba(255,255,255,0.06)',
            padding: '1px 5px',
            borderRadius: 4,
          }}
        >
          {m[6]}
        </code>
      );
    }
    last = re.lastIndex;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

const isBlank = (l: string) => l.trim() === '';
const isHr = (l: string) => /^\s*([-*_])\1{2,}\s*$/.test(l);
const ulRe = /^\s*[-*+]\s+(.*)$/;
const olRe = /^\s*\d+\.\s+(.*)$/;
const hRe = /^(#{1,6})\s+(.*)$/;
const quoteRe = /^\s*>\s?(.*)$/;

// ── Block parser ──────────────────────────────────────────────────────────────
export function Markdown({ content }: { content: string }) {
  const lines = content.replace(/\r\n?/g, '\n').split('\n');
  const blocks: React.ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (isBlank(line)) { i++; continue; }

    // Horizontal rule
    if (isHr(line)) {
      blocks.push(<hr key={key++} style={{ border: 0, borderTop: '0.5px solid rgba(255,255,255,0.1)', margin: '32px 0' }} />);
      i++;
      continue;
    }

    // Heading
    const h = line.match(hRe);
    if (h) {
      const level = h[1].length;
      const inner = renderInline(h[2], `h${key}`);
      if (level === 1) {
        blocks.push(<h1 key={key++} className="text-[26px] sm:text-[32px] font-bold tracking-tight" style={{ color: TEXT, margin: '0 0 20px', lineHeight: 1.2 }}>{inner}</h1>);
      } else if (level === 2) {
        blocks.push(<h2 key={key++} className="text-[18px] sm:text-[21px] font-semibold tracking-tight" style={{ color: TEXT, margin: '36px 0 12px' }}>{inner}</h2>);
      } else {
        blocks.push(<h3 key={key++} className="text-[15px] sm:text-[16px] font-semibold" style={{ color: TEXT, margin: '24px 0 8px' }}>{inner}</h3>);
      }
      i++;
      continue;
    }

    // Unordered list
    if (ulRe.test(line)) {
      const items: React.ReactNode[] = [];
      while (i < lines.length && ulRe.test(lines[i])) {
        const txt = lines[i].match(ulRe)![1];
        items.push(<li key={items.length} style={{ marginBottom: 6 }}>{renderInline(txt, `ul${key}-${items.length}`)}</li>);
        i++;
      }
      blocks.push(
        <ul key={key++} className="text-[15px]" style={{ color: MUTED, lineHeight: 1.7, listStyle: 'disc', paddingLeft: 22, margin: '0 0 18px' }}>
          {items}
        </ul>
      );
      continue;
    }

    // Ordered list
    if (olRe.test(line)) {
      const items: React.ReactNode[] = [];
      while (i < lines.length && olRe.test(lines[i])) {
        const txt = lines[i].match(olRe)![1];
        items.push(<li key={items.length} style={{ marginBottom: 6 }}>{renderInline(txt, `ol${key}-${items.length}`)}</li>);
        i++;
      }
      blocks.push(
        <ol key={key++} className="text-[15px]" style={{ color: MUTED, lineHeight: 1.7, listStyle: 'decimal', paddingLeft: 22, margin: '0 0 18px' }}>
          {items}
        </ol>
      );
      continue;
    }

    // Blockquote
    if (quoteRe.test(line)) {
      const parts: string[] = [];
      while (i < lines.length && quoteRe.test(lines[i])) {
        parts.push(lines[i].match(quoteRe)![1]);
        i++;
      }
      blocks.push(
        <blockquote key={key++} className="text-[15px]" style={{ color: MUTED, lineHeight: 1.7, borderLeft: `2px solid ${ACCENT}`, paddingLeft: 16, margin: '0 0 18px', fontStyle: 'italic' }}>
          {renderInline(parts.join(' '), `q${key}`)}
        </blockquote>
      );
      continue;
    }

    // Paragraph — gather consecutive non-blank, non-special lines
    const para: string[] = [];
    while (
      i < lines.length &&
      !isBlank(lines[i]) &&
      !isHr(lines[i]) &&
      !hRe.test(lines[i]) &&
      !ulRe.test(lines[i]) &&
      !olRe.test(lines[i]) &&
      !quoteRe.test(lines[i])
    ) {
      para.push(lines[i].trim());
      i++;
    }
    blocks.push(
      <p key={key++} className="text-[15px]" style={{ color: MUTED, lineHeight: 1.75, margin: '0 0 18px' }}>
        {renderInline(para.join(' '), `p${key}`)}
      </p>
    );
  }

  return <>{blocks}</>;
}
